import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';

const getHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Content-Type': 'application/json',
    ...(token && { 'Authorization': `Bearer ${token}` })
  };
};

const BuyerInbox = () => {
  const { user } = useAuth();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [replying, setReplying] = useState(false);
  const [replySuccess, setReplySuccess] = useState(false);
  const [filter, setFilter] = useState('all'); // 'all', 'unread', 'read'

  const isAuthenticated = !!(user || localStorage.getItem('token'));

  useEffect(() => {
    if (isAuthenticated) {
      fetchMessages();
    }
  }, [isAuthenticated, filter]);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/favorites/messages/inbox', { headers: getHeaders() });
      if (res.ok) {
        const data = await res.json();
        setMessages(data || []);
      }
    } catch (error) {
      console.error('Error fetching messages:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (messageId) => {
    try {
      await fetch(`/api/favorites/messages/${messageId}/read`, {
        method: 'PUT',
        headers: getHeaders()
      });
      // Update local state
      setMessages(prev => 
        prev.map(m => 
          m._id === messageId ? { ...m, read: true, status: 'opened' } : m
        )
      );
      if (selectedMessage?._id === messageId) {
        setSelectedMessage(prev => ({ ...prev, read: true, status: 'opened' }));
      }
    } catch (error) {
      console.error('Error marking message as read:', error);
    }
  };

  const handleReply = async (messageId) => {
    if (!replyText.trim()) return;
    
    setReplying(true);
    try {
      const res = await fetch(`/api/favorites/messages/${messageId}/reply`, {
        method: 'POST',
        headers: getHeaders(),
        body: JSON.stringify({ content: replyText.trim() })
      });
      if (res.ok) {
        setReplySuccess(true);
        setReplyText('');
        setTimeout(() => setReplySuccess(false), 3000);
        // Refresh messages
        fetchMessages();
      }
    } catch (error) {
      console.error('Error sending reply:', error);
    } finally {
      setReplying(false);
    }
  };

  const getFilteredMessages = () => {
    if (filter === 'unread') {
      return messages.filter(m => !m.read);
    }
    if (filter === 'read') {
      return messages.filter(m => m.read);
    }
    return messages;
  };

  const unreadCount = messages.filter(m => !m.read).length;

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 60 * 60 * 1000) {
      const mins = Math.floor(diff / 60000);
      return `${mins}m ago`;
    }
    if (diff < 24 * 60 * 60 * 1000) {
      const hours = Math.floor(diff / (60 * 60 * 1000));
      return `${hours}h ago`;
    }
    return date.toLocaleDateString('en-AU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  if (!isAuthenticated) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Please login to view your messages</p>
        <Link to="/login" className="text-green-600 hover:underline">Login</Link>
      </div>
    );
  }

  const filteredMessages = getFilteredMessages();

  return (
    <div className="buyer-inbox">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <h3 className="text-sm font-medium text-gray-700">
            📬 Messages
          </h3>
          {unreadCount > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">
              {unreadCount} unread
            </span>
          )}
        </div>
        <div className="flex gap-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          >
            <option value="all">All</option>
            <option value="unread">Unread</option>
            <option value="read">Read</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="border rounded-lg p-4 animate-pulse bg-gray-50 h-20" />
          ))}
        </div>
      ) : messages.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-lg border-2 border-dashed border-gray-200">
          <p className="text-4xl mb-2">📭</p>
          <p className="text-gray-600">Your inbox is empty</p>
          <p className="text-sm text-gray-400 mt-1">
            When vendors you follow send updates, they'll appear here
          </p>
          <Link to="/producers" className="inline-block mt-4 text-green-600 hover:underline">
            Browse Producers →
          </Link>
        </div>
      ) : filteredMessages.length === 0 ? (
        <div className="text-center py-8 text-gray-500">
          <p>No {filter === 'unread' ? 'unread ' : filter === 'read' ? 'read ' : ''}messages</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredMessages.map((message) => (
            <div 
              key={message._id}
              className={`border rounded-lg overflow-hidden hover:shadow-md transition cursor-pointer ${
                !message.read ? 'bg-blue-50 border-blue-200' : 'bg-white'
              }`}
              onClick={() => {
                setSelectedMessage(message);
                if (!message.read) {
                  markAsRead(message._id);
                }
              }}
            >
              <div className="p-4 flex items-start gap-3">
                {!message.read && (
                  <span className="w-2 h-2 mt-2 bg-blue-500 rounded-full flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className="font-medium text-sm">
                        {message.senderName || 'Vendor'}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(message.sentAt)}
                      </span>
                    </div>
                    <span className="text-xs text-gray-400">
                      {message.read ? 'Read' : 'New'}
                    </span>
                  </div>
                  <p className="text-sm font-medium text-gray-800 mt-1 truncate">
                    {message.subject}
                  </p>
                  <p className="text-sm text-gray-600 line-clamp-2">
                    {message.content}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Message Detail Modal */}
      {selectedMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] flex flex-col">
            <div className="flex justify-between items-center p-4 border-b">
              <div>
                <h3 className="font-semibold text-lg">{selectedMessage.subject}</h3>
                <div className="flex items-center gap-2 mt-1">
                  <span className="text-sm text-gray-600">
                    From: {selectedMessage.senderName || 'Vendor'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {new Date(selectedMessage.sentAt).toLocaleString()}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedMessage(null)}
                className="text-gray-500 hover:text-gray-700 text-xl"
              >
                ✕
              </button>
            </div>
            
            <div className="p-4 flex-1 overflow-y-auto">
              <p className="text-gray-800 whitespace-pre-wrap">
                {selectedMessage.content}
              </p>

              {/* Reply Section */}
              <div className="mt-6 pt-4 border-t">
                {replySuccess && (
                  <div className="bg-green-50 text-green-700 p-3 rounded-lg text-sm mb-3">
                    ✅ Reply sent successfully!
                  </div>
                )}
                <div className="flex gap-3">
                  <textarea
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Write a reply..."
                    className="flex-1 border border-gray-300 rounded-lg p-3 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-green-500"
                    rows={2}
                  />
                  <button
                    onClick={() => handleReply(selectedMessage._id)}
                    disabled={!replyText.trim() || replying}
                    className={`
                      px-4 py-2 rounded-lg font-medium transition self-end
                      ${!replyText.trim() || replying
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                      }
                    `}
                  >
                    {replying ? 'Sending...' : 'Reply'}
                  </button>
                </div>
                <p className="text-xs text-gray-400 mt-2">
                  Your reply will be sent directly to the vendor through Our Pickup
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default BuyerInbox;
